/* eslint-disable @typescript-eslint/naming-convention */
import { Phase, StrictOmit } from '@enmon/commons';
import { MeterDetailElectricity } from '@enmon/commons/lib/enmon';
import { Form } from 'antd';
import { FormItemProps } from 'components/Form/FormItem';
import { ElectricityMeterDetailField } from 'components/Form/meter_details/electricity/constants';
import { ElectricityDetails } from 'components/Form/meter_details/electricity/ElectricityDetails';
import { VFC } from 'react';
import { useReplaceMeterStore } from 'routes/meter_replacement/useStore';
import { booleanToSelectValue } from 'utils/booleanToSelectValue';

export type MeterElectricityDetails = StrictOmit<MeterDetailElectricity, 'width' | 'isDirect'> & {
  width: { value: MeterDetailElectricity['width']; unit?: string } | null;
  isDirect: number | null;
};

export interface MeterElectricityDetailsProps extends MeterElectricityDetails {
  onFormItemReset: (name: FormItemProps['name']) => void;
}

export const ElectricityMeterDetails: VFC<MeterElectricityDetailsProps> = props => {
  const {
    onFormItemReset,
    width,
    isDirect,
    measuringCurrentTransformer,
    measurementCategory,
    phases: storePhases,
    mounting,
    tariffs,
    iMax,
    iMin,
    iRef,
    circuitBreakerSize,
    inputSpec,
  } = props;

  const form = Form.useFormInstance();
  const phases = Form.useWatch<Phase | undefined>(['detail', 'phases'], form) ?? storePhases;
  const { setDetailsStepValues, detail } = useReplaceMeterStore();

  // Resets phases and circuit breaker size when phases are reset
  const handlePhaseReset = () => {
    const emptyPhaseAndCircuitBreaker = {
      detail: { ...detail, phases: null, circuitBreakerSize: null },
    };
    form.setFieldsValue(emptyPhaseAndCircuitBreaker);

    // @ts-ignore - Type is correct, but TS can't infer it
    setDetailsStepValues(emptyPhaseAndCircuitBreaker);
  };

  return (
    <ElectricityDetails
      itemsConfig={{
        [ElectricityMeterDetailField.Direct]: {
          formItemProps: {
            initialValue: booleanToSelectValue(isDirect),
            isSemiRequired: true,
            onResetField: onFormItemReset,
          },
        },
        [ElectricityMeterDetailField.MeasurementCategory]: {
          formItemProps: {
            initialValue: measurementCategory,
            isSemiRequired: true,
            onResetField: onFormItemReset,
          },
        },

        ...(!isDirect && {
          [ElectricityMeterDetailField.MCT]: {
            formItemProps: {
              initialValue: measuringCurrentTransformer,
              isSemiRequired: true,
              onResetField: onFormItemReset,
            },
          },
        }),

        [ElectricityMeterDetailField.Phases]: {
          formItemProps: { initialValue: phases, isSemiRequired: true, onResetField: handlePhaseReset },
        },
        [ElectricityMeterDetailField.Mounting]: {
          formItemProps: { initialValue: mounting, isSemiRequired: true, onResetField: onFormItemReset },
        },
        [ElectricityMeterDetailField.Width]: {
          formItemProps: { initialValue: width?.value },
          additionalConfig: { widthUnitProps: { initialValue: width?.unit ?? 'mm' } },
        },
        [ElectricityMeterDetailField.Tariffs]: {
          formItemProps: { initialValue: tariffs, isSemiRequired: true, onResetField: onFormItemReset },
        },

        [ElectricityMeterDetailField.IMin]: {
          formItemProps: { initialValue: iMin, isSemiRequired: true, onResetField: onFormItemReset },
        },
        [ElectricityMeterDetailField.IRef]: {
          formItemProps: { initialValue: iRef, isSemiRequired: true, onResetField: onFormItemReset },
        },
        [ElectricityMeterDetailField.IMax]: {
          formItemProps: { initialValue: iMax, isSemiRequired: true, onResetField: onFormItemReset },
        },
        [ElectricityMeterDetailField.CircuitBreakerSize]: {
          formItemProps: {
            initialValue: circuitBreakerSize,
            isSemiRequired: true,
            onResetField: onFormItemReset,
          },
          additionalConfig: { phases },
        },
        [ElectricityMeterDetailField.InputSpec]: {
          formItemProps: { initialValue: inputSpec },
        },
      }}
    />
  );
};
